import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from './common.js';

// ---------- Gamification events ----------

export const gamificationEventKindSchema = z.enum([
  'completion_early',
  'completion_on_time',
  'completion_late',
  'completion_undated',
  'completion_reverse',
  'shop_spent',
  'shop_refund',
  'admin_grant',
  'overdue_penalty',
  'red_streak_penalty',
]);
export type GamificationEventKind = z.infer<typeof gamificationEventKindSchema>;

export const gamificationEventSchema = z.object({
  id: uuidSchema,
  workspaceUserId: uuidSchema,
  monthKey: z.string().regex(/^\d{4}-\d{2}$/),
  event: gamificationEventKindSchema,
  points: z.number().int(),
  relatedTaskId: uuidSchema.nullable(),
  relatedRequestId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type GamificationEventDto = z.infer<typeof gamificationEventSchema>;

export const tierBadgeSchema = z.object({
  tierIndex: z.number().int().min(0).max(3),
  emoji: z.string().min(1).max(8),
  label: z.string().min(1).max(40),
});
export type TierBadgeDto = z.infer<typeof tierBadgeSchema>;

/** GET /api/gamification/me */
export const gamificationMeResponseSchema = z.object({
  monthKey: z.string().regex(/^\d{4}-\d{2}$/),
  monthlyPoints: z.number().int(),
  heldPoints: z.number().int(),
  /** monthlyPoints − heldPoints — what you can still spend right now. */
  balance: z.number().int(),
  tier: tierBadgeSchema,
  events: z.array(gamificationEventSchema),
  /** Full set of 4 tier badges (with admin overrides applied). */
  tiers: z.array(tierBadgeSchema),
});
export type GamificationMeResponseDto = z.infer<typeof gamificationMeResponseSchema>;

// ---------- Bonus shop catalog ----------

export const bonusShopItemKindSchema = z.enum(['dayOff', 'changedSchedule', 'snack', 'custom']);
export type BonusShopItemKind = z.infer<typeof bonusShopItemKindSchema>;

export const bonusShopItemSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(500).nullable(),
  cost: z.number().int().min(0),
  kind: bonusShopItemKindSchema,
  requiresDate: z.boolean(),
  tierMin: z.number().int().min(0).max(3),
  sortOrder: z.number().int(),
  active: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type BonusShopItemDto = z.infer<typeof bonusShopItemSchema>;

export const createBonusShopItemSchema = z.object({
  name: z.string().min(1, 'Введите название').max(200),
  description: z.string().max(500).nullable().optional(),
  cost: z.coerce.number().int().min(0),
  kind: bonusShopItemKindSchema.default('custom'),
  requiresDate: z.boolean().optional(),
  tierMin: z.coerce.number().int().min(0).max(3).optional(),
  sortOrder: z.coerce.number().int().optional(),
  active: z.boolean().optional(),
});
export type CreateBonusShopItemDto = z.infer<typeof createBonusShopItemSchema>;

export const updateBonusShopItemSchema = createBonusShopItemSchema.partial();
export type UpdateBonusShopItemDto = z.infer<typeof updateBonusShopItemSchema>;

// ---------- Bonus shop requests ----------

export const bonusShopRequestStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'withdrawn',
]);
export type BonusShopRequestStatus = z.infer<typeof bonusShopRequestStatusSchema>;

export const bonusShopRequestSchema = z.object({
  id: uuidSchema,
  employeeId: uuidSchema,
  itemId: uuidSchema,
  pointsHeld: z.number().int().min(0),
  pointsSpent: z.number().int().min(0),
  status: bonusShopRequestStatusSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  reason: z.string().max(255).nullable(),
  createdAt: isoDateTimeSchema,
  decidedAt: isoDateTimeSchema.nullable(),
  decidedById: uuidSchema.nullable(),
});
export type BonusShopRequestDto = z.infer<typeof bonusShopRequestSchema>;

export const createBonusShopRequestSchema = z.object({
  itemId: uuidSchema,
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type CreateBonusShopRequestDto = z.infer<typeof createBonusShopRequestSchema>;

export const transitionBonusShopRequestSchema = z.object({
  // 'revoke' reverses an already-approved request (admin) — refunds the points.
  action: z.enum(['approve', 'reject', 'withdraw', 'revoke']),
  reason: z.string().max(255).optional(),
});
export type TransitionBonusShopRequestDto = z.infer<typeof transitionBonusShopRequestSchema>;

// ---------- Bonus shop tiers ----------

export const upsertTiersSchema = z.object({
  tiers: z.array(tierBadgeSchema),
});
export type UpsertTiersDto = z.infer<typeof upsertTiersSchema>;

/** Admin per-employee tier override (0..3, or null to clear). */
export const setTierOverrideSchema = z.object({
  workspaceUserId: uuidSchema,
  tierOverride: z.number().int().min(0).max(3).nullable(),
});
export type SetTierOverrideDto = z.infer<typeof setTierOverrideSchema>;

/** GET/PATCH /api/bonus-shop/program — workspace gamification module + bonus shop. */
export const bonusProgramSettingsSchema = z.object({
  gamificationEnabled: z.boolean(),
  bonusProgramEnabled: z.boolean(),
});
export type BonusProgramSettingsDto = z.infer<typeof bonusProgramSettingsSchema>;

export const updateBonusProgramSettingsSchema = z
  .object({
    gamificationEnabled: z.boolean().optional(),
    bonusProgramEnabled: z.boolean().optional(),
  })
  .refine(
    (d) => d.gamificationEnabled !== undefined || d.bonusProgramEnabled !== undefined,
    { message: 'Укажите хотя бы один параметр' },
  );
export type UpdateBonusProgramSettingsDto = z.infer<typeof updateBonusProgramSettingsSchema>;
